import React, {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import {CoordinatesSource} from "../../../api";
import { ClockStation } from "../../../Targets";
import { Accordion, Input, InputGroup, InputPicker, MaskedInput, toaster, Toggle } from "rsuite";
import YandexMap from "../../shared/YandexMap/YandexMap";
import { WriteCoordinates } from "../../../Commands";
import { RelaySettingsApi } from "../../../services/RelaySettings.api";
import NotificationFactory from "../../../shared/Notifications";
import {FaArrowDownIcon} from "../../../models/Icons.model";
import {DEFAULT_COORDS, GlonassRelayData, GlonassRelayHandle, GlonassRelayProps, MapCoordinates} from "./RelaySettings.model";

const GlonassRelayControl = forwardRef<GlonassRelayHandle, GlonassRelayProps>(
  (
    {
      executeCommand,
      channel,
      deviceId,
      onAfterWrite,
      dawnTime,
      duskTime
    },
    ref
  ) => {
    const [loading, setLoading] = useState(false);
    const [glonassRelayData, setGlonassRelayData] = useState<GlonassRelayData | null>(null);

    const duskDawnRef = useRef<{ refresh?: () => Promise<void> | void } | null>(null);

    useImperativeHandle(ref, () => ({
      refresh: async () => {
        await getGpsCoordinates();
      },
      update: async () => {
        await writeGlonassRelayCommand();
      }
    }));

    useEffect(() => {
      getGpsCoordinates().then();
    }, [deviceId]);

    const handleCoordinatesFromMap = (coordinates: MapCoordinates | null) => {
      if (!coordinates) return;

      const newGlonassRelayData = structuredClone(
        glonassRelayData || DEFAULT_COORDS
      ) as GlonassRelayData;

      const convertDecimalToDMS = (decimal: number) => {
        const degrees = Math.floor(Math.abs(decimal));
        const minutes = (Math.abs(decimal) - degrees) * 60;

        return `${degrees.toString().padStart(3, "0")}.${Math.round(
          (minutes * 10000) / 60
        )
          .toString()
          .padStart(4, "0")}`;
      };

      newGlonassRelayData.latitude = convertDecimalToDMS(
        Math.abs(parseFloat(coordinates.latitude))
      );
      newGlonassRelayData.longitude = convertDecimalToDMS(
        Math.abs(parseFloat(coordinates.longitude))
      );

      newGlonassRelayData.isManual = "Manual";

      setGlonassRelayData(newGlonassRelayData);
    };

    const getGpsCoordinates = async () => {
      setLoading(true);
      try {
        const response = await RelaySettingsApi.getGpsCoordinates(deviceId as string);

        let coordinates: GlonassRelayData = {
          isManual: (response.coordinateSource ?? "Manual") as "Manual" | "Gps",
          latitudeDirection: (response.latitudeDirection ?? "N") as "N" | "S",
          latitude: response.latitude ?? DEFAULT_COORDS.latitude,
          longitudeDirection: (response.longitudeDirection ?? "E") as "E" | "W",
          longitude: response.longitude ?? DEFAULT_COORDS.longitude
        };

        const latClean = coordinates.latitude.replace(/\D/g, "");
        const lonClean = coordinates.longitude.replace(/\D/g, "");

        if (latClean === "0000000" && lonClean === "0000000") {
          coordinates = DEFAULT_COORDS;
        }

        setGlonassRelayData(coordinates);

        if (duskDawnRef.current?.refresh) {
          await duskDawnRef.current.refresh();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    const getCoordinatesString = (data: GlonassRelayData) => {
      let buffer = "";

      buffer += data.latitudeDirection === "N" ? "+" : "-";
      buffer += data.latitude.replace(/\D/gi, "").padStart(7, "0");
      buffer += data.longitudeDirection === "E" ? "+" : "-";
      buffer += data.longitude.replace(/\D/gi, "").padStart(7, "0");

      return buffer;
    };

    const writeGlonassRelayCommand = async () => {
      setLoading(true);

      try {
        if (glonassRelayData?.isManual === "Manual") {
          const coordinates = getCoordinatesString(glonassRelayData);

          const utf8Encode = new TextEncoder();
          const bytes = utf8Encode.encode(coordinates + "\n");

          if (executeCommand) {
            await executeCommand(ClockStation, WriteCoordinates, bytes);
          }

          await getGpsCoordinates();
          if (onAfterWrite) {
            await onAfterWrite();
          }
        }
      } finally {
        setLoading(false);
      }
    };

    const maskFunction = (rawValue: string) => {
      if (rawValue.replace(/\D/gi, "").length > 6) {
        return [/\d/, /\d/, /\d/, ".", /\d/, /\d/, /\d/, /\d/];
      }
      return [/\d/, /\d/, ".", /\d/, /\d/, /\d/, /\d/];
    };

    const updateCoordinatesSource = async (source: CoordinatesSource) => {
      try {
        const result = await RelaySettingsApi.updateCoordinatesSource(
          deviceId as string,
          source
        );

        if (result) {
          toaster.push(NotificationFactory.success(), {
            placement: "topEnd",
            duration: 3000
          });

          await getGpsCoordinates();
        }
      } catch (err) {
        toaster.push(NotificationFactory.error(), {
          placement: "topEnd",
          duration: 3000
        });
      }
    };

    const renderAstronomicRelayCoordinates = () => {
      return (
        <Fragment>
          <Accordion>
            <Accordion.Panel
              caretAs={FaArrowDownIcon}
              className="mb-2"
              style={{ padding: 0, border: "1px solid #ccc" }}
              header="Выбор точки на карте"
            >
              <YandexMap
                onUseCoordinates={handleCoordinatesFromMap}
                inputCoordinates={{
                  latitudeDirection: glonassRelayData?.latitudeDirection,
                  latitude: glonassRelayData?.latitude,
                  longitudeDirection: glonassRelayData?.longitudeDirection,
                  longitude: glonassRelayData?.longitude
                }}
              />
            </Accordion.Panel>
          </Accordion>

          <div className="row">
            <fieldset
              disabled={
                glonassRelayData === null ||
                glonassRelayData?.isManual !== "Manual"
              }
            >
              <div className="input-group mb-3">
                <InputGroup>
                  <InputGroup.Addon>Широта</InputGroup.Addon>
                  <InputPicker
                    data={[
                      { label: "N", value: "N" },
                      { label: "S", value: "S" }
                    ]}
                    defaultValue="N"
                    value={glonassRelayData?.latitudeDirection}
                    onChange={(value) => {
                      if (!value || !glonassRelayData) return;
                      const newGlonassRelayData = structuredClone(
                        glonassRelayData
                      ) as GlonassRelayData;
                      newGlonassRelayData.latitudeDirection = value as "N" | "S";
                      setGlonassRelayData(newGlonassRelayData);
                    }}
                    style={{ width: "100px" }}
                  />
                  <MaskedInput
                    value={glonassRelayData?.latitude}
                    mask={maskFunction}
                    showMask={false}
                    guide={false}
                    keepCharPositions={false}
                    placeholderChar="_"
                    style={{ width: 300 }}
                    onChange={(value: string) => {
                      if (!glonassRelayData) return;
                      const newGlonassRelayData = structuredClone(
                        glonassRelayData
                      ) as GlonassRelayData;
                      newGlonassRelayData.latitude = value;
                      setGlonassRelayData(newGlonassRelayData);
                    }}
                  />
                </InputGroup>
              </div>
            </fieldset>
          </div>

          <div className="row">
            <fieldset
              disabled={
                glonassRelayData === null ||
                glonassRelayData?.isManual !== "Manual"
              }
            >
              <div className="input-group mb-3">
                <InputGroup>
                  <InputGroup.Addon>Долгота</InputGroup.Addon>
                  <InputPicker
                    data={[
                      { label: "E", value: "E" },
                      { label: "W", value: "W" }
                    ]}
                    defaultValue="E"
                    value={glonassRelayData?.longitudeDirection}
                    onChange={(value) => {
                      if (!value || !glonassRelayData) return;
                      const newGlonassRelayData = structuredClone(
                        glonassRelayData
                      ) as GlonassRelayData;
                      newGlonassRelayData.longitudeDirection = value as "E" | "W";
                      setGlonassRelayData(newGlonassRelayData);
                    }}
                    style={{ width: "100px" }}
                  />
                  <MaskedInput
                    mask={maskFunction}
                    showMask={false}
                    guide={false}
                    keepCharPositions={false}
                    placeholderChar="_"
                    style={{ width: 300 }}
                    value={glonassRelayData?.longitude}
                    onChange={(value: string) => {
                      if (!glonassRelayData) return;
                      const newGlonassRelayData = structuredClone(
                        glonassRelayData
                      ) as GlonassRelayData;
                      newGlonassRelayData.longitude = value;
                      setGlonassRelayData(newGlonassRelayData);
                    }}
                  />
                </InputGroup>
              </div>
            </fieldset>
          </div>
        </Fragment>
      );
    };

    return (
      <>
        <fieldset>
          <div className="row">
            <div className="input-group mb-3 col">
              <Toggle
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                id={`glonassManualInput_${channel}`}
                checked={glonassRelayData?.isManual === "Manual"}
                onChange={async (checked) => {
                  if (!glonassRelayData) return;

                  const newSource = checked ? "Manual" : "Gps";
                  await updateCoordinatesSource(newSource);
                }}
              >
                {glonassRelayData?.isManual === "Manual" ? "Ручной ввод" : "GPS"}
              </Toggle>
            </div>
          </div>

          {glonassRelayData?.isManual === "Manual"
            ? renderAstronomicRelayCoordinates()
            : null}

          <div className="row">
            <div className="col-md-3">
              <InputGroup style={{ marginBottom: "5px", width: "100%" }}>
                <InputGroup.Addon>Восход</InputGroup.Addon>
                <Input className="bg-violet" value={dawnTime ?? ""} readOnly />
              </InputGroup>
            </div>
            <div className="col-md-3">
              <InputGroup style={{ marginBottom: "5px", width: "100%" }}>
                <InputGroup.Addon>Закат</InputGroup.Addon>
                <Input className="bg-violet" value={duskTime ?? ""} readOnly />
              </InputGroup>
            </div>
          </div>
        </fieldset>
      </>
    );
  }
);

export default GlonassRelayControl;
